import { graphql, useStaticQuery } from 'gatsby';
import { FC, useEffect, useState } from 'react';

import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';

import Select from 'molecules/Select/Select';

import JobCard from 'components/Cards/JobCard';

import type { ContentfulComponentSingleInstance } from 'graphqlTypes';

type JobBoardProps = ContentfulComponentSingleInstance;

const locations: string[] = [];
const departments: string[] = [];
const roleType: string[] = [];

const JobBoard: FC<JobBoardProps> = ({ heading }) => {
  const [jobList, setJobList] = useState([]);
  const [location, setLocation] = useState<string | null>(null);
  const [department, setDepartment] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);

  const { allWorkdayJob } = useStaticQuery(graphql`
    query JobPostsQuery {
      allWorkdayJob(filter: { company: { eq: "FieldRoutes" } }) {
        nodes {
          title
          url
          jobFamilyGroup
          jobFamily
          locations: All_Locations {
            locationName
          }
          timeType
          workerSubType
        }
      }
    }
  `);

  const handleLocation = (e: SyntheticEvent) => {
    setLocation(e.target.value);
  };

  const handleDepartment = (e: SyntheticEvent) => {
    setDepartment(e.target.value);
  };

  const handleRole = (e: SyntheticEvent) => {
    setRole(e.target.value);
  };

  useEffect(() => {
    allWorkdayJob?.nodes?.forEach((item: any) => {
      const locs = item?.locations.filter((loc: any) => !locations.map(x => x.value).includes(loc.locationName));

      if (locs?.length) {
        locations.push(...locs.map((loc: any) => ({ value: loc.locationName, label: loc.locationName })));
      }

      if (!departments.map(dep => dep.value).includes(item?.jobFamily) && item?.jobFamily !== undefined) {
        departments.push({ value: item?.jobFamily, label: item?.jobFamily });
      }

      if (!roleType.map(type => type.value).includes(item?.timeType) && item?.timeType !== undefined) {
        roleType.push({ value: item?.timeType, label: item?.timeType });
      }

      return;
    });

    const sortLabel = (a: any, b: any) => (a.label > b.label ? 1 : a.label < b.label ? -1 : 0);

    locations.sort(sortLabel);
    departments.sort(sortLabel);
    roleType.sort(sortLabel);

    setJobList(allWorkdayJob?.nodes);
  }, [allWorkdayJob]);

  const filtered =
    jobList &&
    jobList.filter(
      (job: any) =>
        (location === null || location === ''
          ? 1
          : job?.locations.map((loc: any) => loc.locationName).includes(location)) &&
        (department === null || department === '' ? 1 : job?.jobFamily === department) &&
        (role === null || role === '' ? 1 : job?.timeType === role),
    );

  return (
    <Flex flexDirection="column" justifyContent="space-between" width="100%" gap="32px">
      {heading && (
        <Heading as="h2" textStyle="h3" fontWeight="bold" md={{ textStyle: 'h2', fontWeight: 'bold' }}>
          {heading}
        </Heading>
      )}
      <Flex flexDirection="column" gap="16px" width="100%" md={{ gap: '30px', flexDirection: 'row' }}>
        <Select
          list={locations}
          label="Filter by Location"
          onChange={e => {
            handleLocation(e);
          }}
          placeholderText="Locations"
          fontSize="textXs"
          lineHeight="textXs"
          width="100%"
          minWidth="100%"
          maxWidth="100%"
          data-cy="locations"
          md={{ maxWidth: 'calc((100% - 60px) / 3)', minWidth: 'calc((100% - 60px) / 3)' }}
        />
        <Select
          list={departments}
          label="Filter by Department"
          onChange={e => {
            handleDepartment(e);
          }}
          placeholderText="Department"
          fontSize="textXs"
          lineHeight="textXs"
          width="100%"
          minWidth="100%"
          maxWidth="100%"
          data-cy="departments"
          md={{ maxWidth: 'calc((100% - 60px) / 3)', minWidth: 'calc((100% - 60px) / 3)' }}
        />
        <Select
          list={roleType}
          label="Filter by Role Type"
          onChange={e => {
            handleRole(e);
          }}
          placeholderText="Job Type"
          fontSize="textXs"
          lineHeight="textXs"
          width="100%"
          minWidth="100%"
          maxWidth="100%"
          data-cy="roles"
          md={{ maxWidth: 'calc((100% - 60px) / 3)', minWidth: 'calc((100% - 60px) / 3)' }}
        />
      </Flex>
      {filtered && (
        <Flex flexWrap="wrap" gap="30px">
          {filtered?.map((job: any) => (
            <Flex
              key={job?.title || 'Job Title'}
              width="100%"
              maxWidth="100%"
              md={{ maxWidth: 'calc(50% - 15px)' }}
              lg={{ maxWidth: 'calc((100% - 60px) / 3)' }}
            >
              <JobCard {...job} backgroundIsDark={false} />
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default JobBoard;
